// An iPhone can get its viewport out of step with what's on the screen: after the keyboard goes away, after a sheet
// like the photo picker closes, or when the page changes height under it, most of all in the app added to the home
// screen. Everything pinned to the screen (the bar on top, the tabs at the bottom) then sits in the middle of the
// page until the app is reopened. Two things guard against it: the page is scrolled back to where it can really be,
// which often makes the iPhone lay it out again, and the pinned bars follow the part of the page that's visible,
// whatever the iPhone thinks, through --vv-top and --vv-below.
//
// Both wait while the page is being scrolled. When a scroll bounces past the top or bottom of the page, the iPhone
// reports the visible part as slipping just as it does when it's stuck, though the bars are still where they belong,
// so following it then pushed the bar on top into the middle of the screen and the tabs off the bottom.

export type ViewportState = {
  scrollY: number
  scrollHeight: number
  innerHeight: number
  // How tall the box that pinned things are laid out against really is, and the tallest the page has been at this
  // width when no one was typing (in the home-screen app). In an iPhone home-screen app since iOS 26 the reported
  // page height shrinks with the keyboard, though a sheet pinned to the bottom still sits at the bottom of the
  // screen, behind the keyboard.
  fixedHeight: number
  tallest: number
  // The visible part of the page: how far it starts below the top of the layout, how tall it is, and how far it's
  // pinched in.
  offsetTop: number
  height: number
  scale: number
  typing: boolean
}

// Where to scroll to put things right, or null when nothing is wrong (or someone is typing or zoomed in on purpose).
export function settledScroll(v: ViewportState): number | null {
  if (v.typing || v.scale > 1.01) return null
  const furthest = Math.max(0, v.scrollHeight - v.innerHeight)
  if (v.offsetTop < 1 && v.scrollY <= furthest) return null
  return Math.min(v.scrollY, furthest)
}

// How far to move the bar on top down, and the tabs at the bottom down, so they sit at the edges of what's visible.
// While typing, the tabs stay put behind the keyboard rather than riding up on it, but a sheet rises above the
// keyboard, as it does in a phone's own apps: keyboard is how much of the bottom of the layout it covers.
export function pinnedShift(v: ViewportState): { top: number; below: number; keyboard: number } {
  if (v.scale > 1.01) return { top: 0, below: 0, keyboard: 0 }
  const top = v.typing ? 0 : Math.max(0, Math.round(v.offsetTop))
  // In the home-screen app the box pinned things sit against can also stay short after the keyboard or the photo
  // picker has gone, with the visible part reporting itself just as short, though the page still fills the screen:
  // the tabs then float mid-screen. The tallest the page has been is where the bottom of the screen really is.
  const short = v.typing ? 0 : v.tallest - v.fixedHeight
  const below = Math.max(0, Math.round(v.offsetTop + v.height - v.innerHeight), Math.round(short))
  const layout = Math.max(v.innerHeight, v.fixedHeight, v.tallest)
  const keyboard = v.typing ? Math.max(0, Math.round(layout - v.offsetTop - v.height)) : 0
  return { top, below, keyboard }
}

const typingIn = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    (el instanceof HTMLInputElement &&
      !/^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/.test(el.type)))

// How long the page has to be still before a scroll counts as over.
const SCROLL_QUIET = 150
// How much room to leave under a field being typed in.
const REVEAL_GAP = 16

export function keepPinnedOnScreen() {
  const vv = window.visualViewport
  if (!vv) return
  const root = document.documentElement
  // An invisible box pinned to the edges of the screen, to measure what pinned things are laid out against.
  const probe = document.createElement('div')
  probe.setAttribute('aria-hidden', 'true')
  Object.assign(probe.style, { position: 'fixed', top: '0', bottom: '0', width: '0', visibility: 'hidden' })
  document.body.append(probe)
  const installed = window.matchMedia('(display-mode: standalone)')
  let tallest = { width: 0, height: 0 }
  const state = (): ViewportState => {
    const typing = typingIn(document.activeElement)
    const width = window.innerWidth
    if (width !== tallest.width) tallest = { width, height: 0 }
    // Only in the home-screen app: in a browser tab the page grows when the toolbar tucks away, which isn't the
    // keyboard going.
    if (!typing && vv.scale <= 1.01 && installed.matches) tallest.height = Math.max(tallest.height, window.innerHeight)
    return {
      scrollY: window.scrollY,
      scrollHeight: root.scrollHeight,
      innerHeight: window.innerHeight,
      fixedHeight: probe.getBoundingClientRect().height,
      tallest: tallest.height,
      offsetTop: vv.offsetTop,
      height: vv.height,
      scale: vv.scale,
      typing,
    }
  }
  // Inside a sheet, the field being typed in is kept clear of the keyboard and the bar iOS puts above it.
  const reveal = () => {
    const field = document.activeElement
    const sheet = field?.closest('[data-slot="dialog-content"]')
    if (!sheet || !typingIn(field)) return
    const visibleBottom = Math.min(vv.offsetTop + vv.height, sheet.getBoundingClientRect().bottom)
    const below = field!.getBoundingClientRect().bottom + REVEAL_GAP - visibleBottom
    if (below > 0) sheet.scrollTop += below
  }
  // A finger on the screen, or the page still gliding or bouncing from one.
  let touching = false
  let lastScroll = 0
  let quiet = 0
  const scrolling = () => touching || performance.now() - lastScroll < SCROLL_QUIET
  // What to do once the page is still: settle (which ends by following) if either was put off, else just follow.
  let waiting: (() => void) | null = null
  const afterScrolling = (then: () => void) => {
    if (waiting !== settle) waiting = then
    window.clearTimeout(quiet)
    quiet = window.setTimeout(() => {
      if (scrolling()) return afterScrolling(waiting!)
      const run = waiting!
      waiting = null
      run()
    }, SCROLL_QUIET)
  }
  let frame = 0
  const follow = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      const v = state()
      // While typing the keyboard can move the page at any time, and a sheet has to ride above it straight away.
      if (!v.typing && scrolling()) return afterScrolling(follow)
      const { top, below, keyboard } = pinnedShift(v)
      root.style.setProperty('--vv-top', `${top}px`)
      root.style.setProperty('--vv-below', `${below}px`)
      root.style.setProperty('--keyboard', `${keyboard}px`)
      if (keyboard > 0) requestAnimationFrame(reveal)
    })
  }
  let timer = 0
  const settle = () => {
    if (scrolling()) return afterScrolling(settle)
    const to = settledScroll(state())
    if (to !== null) window.scrollTo(window.scrollX, to)
    follow()
  }
  // Once now and once after the keyboard or sheet has finished sliding away and any dialog has finished closing.
  const soon = () => {
    window.clearTimeout(timer)
    requestAnimationFrame(settle)
    timer = window.setTimeout(settle, 400)
  }
  const scrolled = () => {
    lastScroll = performance.now()
    follow()
  }
  const touched = (e: TouchEvent) => {
    touching = e.touches.length > 0
    if (!touching) follow()
  }
  window.addEventListener('touchstart', touched, { passive: true })
  window.addEventListener('touchend', touched, { passive: true })
  window.addEventListener('touchcancel', touched, { passive: true })
  vv.addEventListener('resize', soon)
  window.addEventListener('resize', soon)
  vv.addEventListener('scroll', scrolled)
  window.addEventListener('scroll', scrolled, { passive: true })
  window.addEventListener('focus', soon)
  document.addEventListener('focusin', follow)
  document.addEventListener('focusout', soon)
  document.addEventListener('visibilitychange', soon)
  new ResizeObserver(soon).observe(document.body)
}
